import type { ModifyClientRequest, OrderDetailsRequest } from 'apiConnector/secondary-market/cp-order/model';
import type { Custodian } from 'models/custodian';
import type { FormErrorSeverity } from 'models/formError';
import { createSelector } from 'reselect';
import { getOriginalFormSettlementType } from 'state/secondaryMarket/cpOrder/cpOrderForm/original/cpOrderOriginalForm.selector';
import {
  getAllSales,
  getClientDefaultSettlementType,
  getClientError,
} from 'state/secondaryMarket/cpOrder/referential/client/client.selector';
import {
  getCpOrderReferentialLotSize,
  getCpOrderReferentialMaxQuantityBuy,
  getCpOrderReferentialMaxQuantitySell,
  getCpOrderReferentialMinQuantity,
} from 'state/secondaryMarket/cpOrder/referential/product/product.selector';
import * as fromSecondaryMarket from 'state/secondaryMarket/secondaryMarket.selectors';
import type { SecondaryMarketFormError } from 'state/secondaryMarket/shared/form/validationResults';
import {
  MANDATORY_FIELDS_WARNING,
  MISSING_CASH_AMOUNT_OR_QUANTITY_ERROR,
  MISSING_CLIENT_ERROR,
  MISSING_CUSTODIAN_ERROR,
  MISSING_SALES_DESK_ERROR,
  MISSING_SALES_ERROR,
  MISSING_SETTLEMENT_DATE_ERROR,
  MISSING_SETTLEMENT_TYPE_ERROR,
  MISSING_TRADE_WAY_ERROR,
  NO_MODIFIED_FIELDS_WARNING,
} from 'state/secondaryMarket/shared/form/validationResults';
import { addQuantityInfoMessages } from 'state/secondaryMarket/shared/selector';
import { getBcsProductKeyByWorkspace } from 'state/selectors/common/tabs';
import type { AppState } from 'state/state.model';
import { getWorkspaceType } from 'state/workspaces/tabs/tabs.selector';

import { getAssociatedSalesDesk, getCobDate } from '../cpOrder.utils';
import type { CpOrderSettlementType } from '../referential/client/client.model';
import {
  getBookingParamsAvailableValuationDates,
  getErrorMessages,
  getInfoMessages,
  getWarningMessages,
} from '../referential/product/product.selector.domino';
import type { SecondaryMarketCpOrderForm } from './input/cpOrderInputForm.model';
import { defaultCpOrderForm } from './input/cpOrderInputForm.model';
import {
  getInputFormCashAmount,
  getInputFormClient,
  getInputFormClientType,
  getInputFormComment,
  getInputFormCustodian,
  getInputFormCustodianBdrId,
  getInputFormQuantity,
  getInputFormSalesId,
  getInputFormSalesMargin,
  getInputFormSettlementDate,
  getInputFormTradeWay,
  getInputFormTradingPrice,
  getInputFormValuationDate,
  getInputTradeDateType,
} from './input/cpOrderInputForm.selectors';

export { isCpOrderToAmendLoaded } from './original/cpOrderOriginalForm.selector';

const mapMessagesWithSeverity = (messages: string[], severity: FormErrorSeverity) =>
  messages?.map(
    m =>
      ({ fields: [], severity, message: m, values: {} }) satisfies SecondaryMarketFormError<SecondaryMarketCpOrderForm>,
  );

function initErrors(errorMessages: string[], warningMessages: string[], infoMessages: string[]) {
  const transformedErrorMessages = mapMessagesWithSeverity(errorMessages, 'Failure');
  const transformedWarningMessages = mapMessagesWithSeverity(warningMessages, 'Error');
  const transformedInfoMessages = mapMessagesWithSeverity(infoMessages, 'Warning');
  return [...transformedErrorMessages, ...transformedWarningMessages, ...transformedInfoMessages];
}

const _getEveryFormData: (state: AppState) => Map<string, SecondaryMarketCpOrderForm> = createSelector(
  [(s: AppState) => s.secondaryMarket.cpOrder.form.input, (s: AppState) => s.secondaryMarket.cpOrder.form.original],
  (inputForms, originalForms) => {
    const result: [string, SecondaryMarketCpOrderForm][] = Object.entries(inputForms).map(([tabId, inputForm]) => {
      const originalForm = originalForms[tabId];
      const form = { ...defaultCpOrderForm, ...originalForm, ...inputForm };
      return [tabId, form] as [string, SecondaryMarketCpOrderForm];
    });
    return new Map<string, SecondaryMarketCpOrderForm>(result);
  },
);

const _getFormData: (appState: AppState, tabId: string) => SecondaryMarketCpOrderForm | undefined = createSelector(
  [(s: AppState, _tabId: string) => _getEveryFormData(s), (_s: AppState, tabId: string) => tabId],
  (forms, tabId) => {
    const result: SecondaryMarketCpOrderForm | undefined = forms.get(tabId);
    return result;
  },
);

export function getFormQuantity(appState: AppState, tabId: string) {
  const form = _getFormData(appState, tabId);
  return form?.quantity;
}

export function getFormValuationDate(appState: AppState, tabId: string) {
  const form = _getFormData(appState, tabId);
  return form?.valuationDate;
}

export function getFormCashAmount(appState: AppState, tabId: string) {
  const form = _getFormData(appState, tabId);
  return form?.cashAmount;
}

export function getFormClientType(appState: AppState, tabId: string) {
  const form = _getFormData(appState, tabId);
  return form?.clientType;
}

export function getFormSalesId(appState: AppState, tabId: string) {
  const form = _getFormData(appState, tabId);
  return form?.salesId;
}

export function getFormSalesDesk(appState: AppState, tabId: string) {
  const form = _getFormData(appState, tabId);
  const salesDesk = form?.salesDesk;
  if (salesDesk !== undefined) {
    return salesDesk;
  }
  const salesId = form?.salesId;
  const allSales = getAllSales(appState, tabId);
  return getAssociatedSalesDesk(allSales, salesId);
}

export function getFormClient(appState: AppState, tabId: string) {
  const form = _getFormData(appState, tabId);
  return form?.client;
}

export function getFormClientBdrId(appState: AppState, tabId: string) {
  const client = getFormClient(appState, tabId);
  return client?.id;
}

export function getFormClientEliotCode(appState: AppState, tabId: string) {
  const client = getFormClient(appState, tabId);
  return client?.eliotCode;
}

export function hasSelectedClient(appState: AppState, tabId: string) {
  const form = _getFormData(appState, tabId);
  return form?.clientType === 'Other' && (form?.client?.eliotCode !== undefined || form?.client?.id !== undefined);
}

export function getFormCustodianBdrId(appState: AppState, tabId: string) {
  return getFormCustodian(appState, tabId)?.id;
}

export function getFormCustodian(appState: AppState, tabId: string): Custodian | undefined {
  const form = _getFormData(appState, tabId);
  return form?.custodian;
}

export function getFormTradingPrice(appState: AppState, tabId: string) {
  const form = _getFormData(appState, tabId);
  return form?.tradingPrice;
}

export function getFormComment(appState: AppState, tabId: string) {
  const form = _getFormData(appState, tabId);
  return form?.comment;
}

export function getFormUpfront(appState: AppState, tabId: string) {
  const form = _getFormData(appState, tabId);
  return form?.upfront;
}

export function getFormSalesMargin(appState: AppState, tabId: string) {
  const form = _getFormData(appState, tabId);
  return form?.salesMargin;
}

export function getFormSettlementDate(appState: AppState, tabId: string) {
  const form = _getFormData(appState, tabId);
  return form?.settlementDate;
}

export function getFormFirstSettlementDate(appState: AppState, tabId: string) {
  const form = _getFormData(appState, tabId);
  return form?.firstSettlementDate;
}

export function getTradeDateType(appState: AppState, tabId: string) {
  const form = _getFormData(appState, tabId);
  return form?.tradeDateType;
}

export function getFormTradeWay(appState: AppState, tabId: string) {
  const form = _getFormData(appState, tabId);
  return form?.tradeWay;
}

export function hasNoSelectedTradeWay(appState: AppState, tabId: string) {
  const form = _getFormData(appState, tabId);
  if (form?.tradeWay === undefined || form?.tradeWay === null) {
    return true;
  }
  return false;
}

export function getFormSettlementType(appState: AppState, tabId: string): CpOrderSettlementType | undefined {
  const form = _getFormData(appState, tabId);
  const defaultSettlementType = getClientDefaultSettlementType(appState, tabId, form?.tradeWay);
  return form?.settlementType ?? defaultSettlementType;
}

export function getFormNeedCustodian(appState: AppState, tabId: string) {
  const form = _getFormData(appState, tabId);
  return form?.clientType === 'Other';
}

export function getPlaceOrderRequestOrderDetails(state: AppState, tabId: string): OrderDetailsRequest {
  const client = getFormClient(state, tabId);

  const valuationDate = getFormValuationDate(state, tabId);
  const tradeSizeInputMode = fromSecondaryMarket.cpOrder.formInput.getInputFormTradeSizeInputMode(state, tabId);

  return {
    client: client,
    settlementType: getFormSettlementType(state, tabId),
    cashAmount: tradeSizeInputMode === 'amount' ? getFormCashAmount(state, tabId) : undefined,
    clientType: getFormClientType(state, tabId),
    comment: getFormComment(state, tabId),
    custodian: getFormCustodian(state, tabId),
    quantity: tradeSizeInputMode === 'quantity' ? getFormQuantity(state, tabId) : undefined,
    salesId: getFormSalesId(state, tabId),
    tradingPrice: getFormTradingPrice(state, tabId),
    salesMargin: getFormSalesMargin(state, tabId),
    settlementDate: getFormSettlementDate(state, tabId),
    tradeWay: getFormTradeWay(state, tabId),
    valuationDate,
    cobDate: getCobDate(valuationDate),
    tradeDateType: getTradeDateType(state, tabId),
  } as OrderDetailsRequest;
}

export function getAmendOrderRequestOrderDetails(state: AppState, tabId: string): OrderDetailsRequest {
  const valuationDate = getInputFormValuationDate(state, tabId);

  const inputMode = fromSecondaryMarket.cpOrder.formInput.getInputFormTradeSizeInputMode(state, tabId);
  const settlementType = getFormSettlementType(state, tabId);
  const originalSettlementType = getOriginalFormSettlementType(state, tabId);

  const request = {
    client: getInputFormClient(state, tabId),
    settlementType: originalSettlementType !== settlementType ? settlementType : undefined,
    cashAmount: inputMode === 'amount' ? getInputFormCashAmount(state, tabId) : undefined,
    clientType: getInputFormClientType(state, tabId),
    comment: getInputFormComment(state, tabId),
    custodian: getInputFormCustodian(state, tabId),
    quantity: inputMode === 'quantity' ? getInputFormQuantity(state, tabId) : undefined,
    salesId: getInputFormSalesId(state, tabId),
    tradingPrice: getInputFormTradingPrice(state, tabId),
    salesMargin: getInputFormSalesMargin(state, tabId),
    settlementDate: getInputFormSettlementDate(state, tabId),
    tradeWay: getInputFormTradeWay(state, tabId),
    valuationDate,
    cobDate: getCobDate(valuationDate),
    tradeDateType: getInputTradeDateType(state, tabId),
  } as OrderDetailsRequest;

  if (request.custodian !== undefined || request.salesId !== undefined) {
    return {
      ...request,
      clientType: getFormClientType(state, tabId),
      client: getFormClient(state, tabId),
      salesId: getFormSalesId(state, tabId),
      custodian: getFormCustodian(state, tabId),
    };
  }

  return request;
}

export function getModifyClientRequest(state: AppState, tabId: string): ModifyClientRequest {
  const workspaceType = getWorkspaceType(state, tabId)!;
  const bcsProductKey = getBcsProductKeyByWorkspace(state, workspaceType, tabId)!;
  const clientType = fromSecondaryMarket.cpOrder.form.getFormClientType(state, tabId)!;
  const client = fromSecondaryMarket.cpOrder.form.getFormClient(state, tabId);
  const custodianBdrId = fromSecondaryMarket.cpOrder.form.getFormCustodianBdrId(state, tabId)!;
  const salesId = fromSecondaryMarket.cpOrder.form.getFormSalesId(state, tabId);
  return {
    bcsProductKey,
    clientType,
    client,
    custodianBdrId,
    salesId,
  };
}

const mandatoryFieldsErrors = [
  'clientMissing',
  'missingSales',
  'missingSalesTeam',
  'missingCashAmountOrQuantity',
  'missingValuationDate',
  'missingSettlementDate',
  'missingCustodian',
  'missingSettlementType',
  'missingTradeWay',
];

export type GetValidationErrorsFunc = (
  state: AppState,
  tabId: string,
) => SecondaryMarketFormError<SecondaryMarketCpOrderForm>[];

const INCONSISTENT_VALUATION_DATE: SecondaryMarketFormError<SecondaryMarketCpOrderForm> = {
  fields: ['valuationDate'],
  severity: 'Error',
  translationKey: 'inconsistentValuationDate',
  values: {},
  isDisplayedInSummary: false,
};
const MISSING_VALUATION_DATE: SecondaryMarketFormError<SecondaryMarketCpOrderForm> = {
  fields: ['valuationDate'],
  severity: 'Error',
  translationKey: 'missingValuationDate',
  values: {},
  isDisplayedInSummary: false,
};
export const makeGetBackendReports = () =>
  createSelector(
    [
      (appState: AppState, tabId: string) => getErrorMessages(appState, tabId),
      (appState: AppState, tabId: string) => getWarningMessages(appState, tabId),
      (appState: AppState, tabId: string) => getInfoMessages(appState, tabId),
      (appState: AppState, tabId: string) => getClientError(appState, tabId),
    ],
    (errorMessages, warningMessages, infoMessages, clientError) => {
      const errors = initErrors(errorMessages, warningMessages, infoMessages);
      if (clientError) {
        return [...errors, clientError];
      }
      return errors;
    },
  );
export const getMaxQuantity = (state: AppState, tabId: string) =>
  getFormTradeWay(state, tabId) === 'ClientBuy'
    ? getCpOrderReferentialMaxQuantitySell(state, tabId)
    : getCpOrderReferentialMaxQuantityBuy(state, tabId);
export const makeGetCpOrderValidationErrors: () => GetValidationErrorsFunc = () =>
  createSelector(
    [
      hasSelectedClient,
      getFormQuantity,
      getFormCashAmount,
      getFormClientType,
      getFormSalesId,
      getFormSalesDesk,
      getFormValuationDate,
      getBookingParamsAvailableValuationDates,
      getFormSettlementDate,
      getFormCustodianBdrId,
      getFormSettlementType,
      getFormNeedCustodian,
      hasNoSelectedTradeWay,
      getCpOrderReferentialLotSize,
      getMaxQuantity,
      getCpOrderReferentialMinQuantity,
    ],
    (
      hasSelectedClient,
      quantity,
      cashAmount,
      clientType,
      salesId,
      salesDesk,
      valuationDate,
      availableValuationDates,
      settlementDate,
      custodianId,
      settlementType,
      hasAvailableCustodians,
      hasNoSelectedTradeWay,
      lotSize,
      maxQuantity,
      minQuantity,
    ) => {
      const errors: SecondaryMarketFormError<SecondaryMarketCpOrderForm>[] = [];

      if (clientType === 'Other' && !hasSelectedClient) {
        errors.push(MISSING_CLIENT_ERROR);
      }

      if (quantity === undefined && cashAmount === undefined) {
        errors.push(MISSING_CASH_AMOUNT_OR_QUANTITY_ERROR);
      }

      if (hasSelectedClient && salesDesk === undefined) {
        errors.push(MISSING_SALES_DESK_ERROR);
      }

      if (hasSelectedClient && salesDesk !== undefined && salesId === undefined) {
        errors.push(MISSING_SALES_ERROR);
      }

      if (valuationDate === undefined) {
        errors.push(MISSING_VALUATION_DATE);
      }

      if (valuationDate !== undefined && availableValuationDates?.includes(valuationDate) === false) {
        errors.push(INCONSISTENT_VALUATION_DATE);
      }

      if (settlementDate === undefined) {
        errors.push(MISSING_SETTLEMENT_DATE_ERROR);
      }

      if (hasSelectedClient && custodianId === undefined && hasAvailableCustodians) {
        errors.push(MISSING_CUSTODIAN_ERROR);
      }

      if (settlementType === undefined) {
        errors.push(MISSING_SETTLEMENT_TYPE_ERROR);
      }

      if (hasNoSelectedTradeWay) {
        errors.push(MISSING_TRADE_WAY_ERROR);
      }

      addQuantityInfoMessages(quantity, minQuantity, maxQuantity, lotSize, errors);

      if (errors.map(e => e.translationKey).some(e => e && mandatoryFieldsErrors.includes(e))) {
        errors.push(MANDATORY_FIELDS_WARNING);
      }

      return errors;
    },
  );

type GetModifiedFields = (state: AppState, tabId: string) => string[];

export const makeGetModifiedFields: () => GetModifiedFields = () =>
  createSelector(
    [
      getInputFormQuantity,
      getInputFormCashAmount,
      getInputFormClientType,
      getInputFormClient,
      getInputFormCustodianBdrId,
      getInputFormSalesId,
      getInputFormSalesMargin,
      getInputFormTradingPrice,
      getInputFormValuationDate,
      getInputFormSettlementDate,
      getFormSettlementType,
      getOriginalFormSettlementType,
      getInputFormComment,
      getInputTradeDateType,
    ],
    (
      quantity,
      cashAmount,
      clientType,
      client,
      custodianBdrId,
      salesId,
      salesMargin,
      tradingPrice,
      valuationDate,
      settlementDate,
      formSettlementType,
      originalSettlementType,
      comment,
      tradeDateType,
    ) => {
      const modified: string[] = [];
      if (quantity !== undefined) {
        modified.push('quantity');
      }
      if (cashAmount !== undefined) {
        modified.push('cashAmount');
      }
      if (clientType !== undefined) {
        modified.push('clientType');
      }
      if (client !== undefined) {
        modified.push('client');
      }
      if (custodianBdrId !== undefined) {
        modified.push('custodian');
      }
      if (valuationDate !== undefined) {
        modified.push('valuationDate');
      }
      if (settlementDate !== undefined) {
        modified.push('settlementDate');
      }
      if (formSettlementType?.settlementType !== originalSettlementType?.settlementType) {
        modified.push('settlementType');
      }
      if (salesId !== undefined) {
        modified.push('sales');
      }
      if (salesMargin !== undefined) {
        modified.push('salesMargin');
      }
      if (tradingPrice !== undefined) {
        modified.push('tradingPrice');
      }
      if (comment !== undefined) {
        modified.push('comment');
      }
      if (tradeDateType !== undefined) {
        modified.push('tradeDateType');
      }

      return modified;
    },
  );

const NULL_QUANTITY_ERROR: SecondaryMarketFormError<SecondaryMarketCpOrderForm> = {
  fields: ['quantity'],
  severity: 'Error',
  translationKey: 'cancelInstead',
  values: {},
  isDisplayedInSummary: false,
};
const NULL_CASH_AMOUNT_ERROR: SecondaryMarketFormError<SecondaryMarketCpOrderForm> = {
  fields: ['cashAmount'],
  severity: 'Error',
  translationKey: 'cancelInstead',
  values: {},
  isDisplayedInSummary: false,
};
export const makeGetAmendCpOrderValidationErrors: (
  getModifiedFields: GetModifiedFields,
) => GetValidationErrorsFunc = getModifiedFields =>
  createSelector(
    [
      getModifiedFields,
      getInputFormQuantity,
      getInputFormCashAmount,
      getFormClientType,
      getFormSalesId,
      getFormCustodianBdrId,
      getCpOrderReferentialLotSize,
      getMaxQuantity,
      getCpOrderReferentialMinQuantity,
    ],
    (modified, quantity, cashAmount, clientType, salesId, custodianBdrId, lotSize, maxQuantity, minQuantity) => {
      const errors: SecondaryMarketFormError<SecondaryMarketCpOrderForm>[] = [];

      if (quantity === 0) {
        errors.push(NULL_QUANTITY_ERROR);
      }
      if (cashAmount === 0) {
        errors.push(NULL_CASH_AMOUNT_ERROR);
      }
      if (modified.length === 0) {
        errors.push(NO_MODIFIED_FIELDS_WARNING);
      }
      if (clientType === 'Other' && custodianBdrId === undefined) {
        errors.push(MISSING_CUSTODIAN_ERROR);
      }
      if (clientType === 'Other' && salesId === undefined) {
        errors.push(MISSING_SALES_ERROR);
      }
      addQuantityInfoMessages(quantity, minQuantity, maxQuantity, lotSize, errors);
      return errors;
    },
  );
